/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * `{name}` placeholders in translated copy, filled the same way everywhere.
 *
 * Author and laadpalen brand pages carry a proper noun in their title,
 * description and H1 ("{brand} laadpalen en MID-meter"; the province pages
 * that did too went on 2026-10-07). Both SEO mirrors, the client
 * hook and the page itself fill those; one helper so an unknown placeholder is
 * left visible in all four (an editor sees `{provinse}` and fixes the key)
 * rather than silently blanked by one of them.
 *
 * The meta-description cap lives here too (2026-10-04): it is the other rule
 * every head builder applies to copy it did not get from an editor, and this
 * is the one zero-import module the resolvers may share — chargerCatalog.js
 * imports nothing but its enums and this file (chargerCatalogSplit.test.ts).
 */

/**
 * Longest meta description a head builder derives from content (a term's
 * definition, an article's excerpt, a provider's intro). Result pages cut at
 * about this length; text an editor typed as the page's description is never
 * cut to it.
 */
export const META_DESCRIPTION_MAX = 160;

/**
 * Collapse whitespace and cut at the last word boundary before `max`, with an
 * ellipsis. A text without a usable boundary (a 200-character token) is cut
 * hard. Non-strings become ''.
 *
 * @param {unknown} text
 * @param {number} [max]
 * @returns {string}
 */
export function clampMetaDescription(text, max = META_DESCRIPTION_MAX) {
  const flat = typeof text === 'string' ? text.replace(/\s+/g, ' ').trim() : '';
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  const head = space > max * 0.6 ? cut.slice(0, space) : cut;
  return `${head.replace(/[\s,;:–—-]+$/, '')}…`;
}

/**
 * Dotted-path lookup over a merged translations tree, for the code that builds
 * a head outside React: the head (src/lib/seoHead.js) and its JSON-LD
 * (src/lib/routeHeadSchema.js). A missing key — or one that names no string —
 * answers the key path itself, the contract of the client's `t()`, so the
 * builders' "is this a real value" guards read both the same way. The head and
 * the bake each kept a copy until 2026-10-14, and that pair answered a
 * non-string value with the value.
 *
 * @param {unknown} translations
 * @param {string} key
 * @returns {string}
 */
export function tLookup(translations, key) {
  const value = key
    .split('.')
    .reduce(
      (o, k) =>
        o && typeof o === 'object' ? /** @type {Record<string, unknown>} */ (o)[k] : undefined,
      /** @type {unknown} */ (translations)
    );
  return typeof value === 'string' ? value : key;
}

/** What the site puts behind every title that does not already name it. */
export const BRAND_SUFFIX = ' | ERE-Vergelijk';

/**
 * A page title with the site's name after it — unless the title already names
 * the site ("… | ERE-Vergelijk.nl"), which is then not branded twice. ONE rule
 * for every title: the head the bake and the server inject (src/lib/seoHead.js),
 * the tab title after hydration (useSEO, ArticlePage), the RSS channel and the
 * SEO tab's length counter, which measures the whole title as Google shows it.
 *
 * @param {string} raw
 * @returns {string}
 */
export function brandTitle(raw) {
  return /ere-vergelijk/i.test(raw) ? raw : `${raw}${BRAND_SUFFIX}`;
}

/**
 * @param {unknown} template  The translated string. Non-strings become ''.
 * @param {Record<string, string | number | null | undefined>} vars
 * @returns {string}
 */
export function fillTemplate(template, vars) {
  if (typeof template !== 'string') return '';
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, key) => {
    const value = vars ? vars[key] : undefined;
    return value === undefined || value === null ? match : String(value);
  });
}
